<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-body">
                <div class="row">
                    <div class="col-12">
                        <form
                            class="form-inline job-search-form"
                            @submit.prevent="searchChanged"
                        >
                            <div class="input-group col-sm-4">
                                <label for="keyword">Search</label>
                                <input
                                    type="text"
                                    class="form-control mb-2 mr-sm-2"
                                    placeholder="Search"
                                    id="keyword"
                                    v-model.lazy="keyword"
                                    @keyup="searchChanged"
                                >
                            </div>
                            <div class="input-group col-sm-2">
                                <label for="start_date">Start Date</label>
                                <date-picker
                                    id="start_date"
                                    :options="{format: 'L'}"
                                    required
                                    :value="start_date"
                                    @input="dateChanged"
                                />
                            </div>
                            <div class="input-group col-sm-2">
                                <label for="status">Status</label>
                                <select
                                    name="status"
                                    id="status"
                                    placeholder="Status"
                                    v-model="status"
                                    class="form-control"
                                    @change="searchChanged"
                                >
                                    <option :value="null">
                                        Any Status
                                    </option>
                                    <option
                                        v-for="s in statuses"
                                        :key="s.value"
                                        :value="s.value"
                                    >
                                        {{ s.name }}
                                    </option>
                                </select>
                            </div>
                            <button class="btn btn-primary mb-2 mr-sm-2">
                                <i class="fa fa-search" /> Search
                            </button>
                            <button
                                class="btn btn-secondary mb-2 mr-sm-2"
                                type="button"
                                @click="resetSearch"
                            >
                                <i class="fa fa-times" /> Clear
                            </button>
                            <router-link
                                to="/office/jobs/schedule"
                                class="btn btn-success mb-2 mr-sm-2"
                            >
                                <i class="fa fa-plus" /> New Job
                            </router-link>
                            <i
                                class="fa fa-spinner fa-pulse mb-2 mr-sm-2"
                                v-if="loading"
                            />
                        </form>
                    </div>
                </div>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>Job Name</th>
                            <th>Customer</th>
                            <th>Site</th>
                            <th>Call Out</th>
                            <th>Starts At</th>
                            <th>Status</th>
                            <th>Last Updated</th>
                            <th>
                                <!-- buttons -->
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(job, index) in jobs"
                            :key="index"
                        >
                            <td>
                                <router-link :to="{ name: 'office.jobs.show', params: { id: job.id } }">
                                    {{ job.name }}
                                </router-link>
                            </td>
                            <td>
                                <router-link
                                    :to="{ name:'office.customers.show', params: { id: job.customer.id } }"
                                    title="View Customer"
                                >
                                    {{ job.customer.company_name }}
                                    <i
                                        class="fas fa-external-link-alt"
                                        style="font-size: .8em; margin-left: .5em"
                                    />
                                </router-link>
                            </td>
                            <td>
                                <span v-if="!job.site">-</span>
                                <span v-if="job.site">{{ job.site.name }}</span>
                            </td>
                            <td>
                                <span v-if="job.is_call_out">Y</span>
                                <span v-else>N</span>
                            </td>
                            <td v-if="job.first_time_slot">
                                {{ job.first_time_slot.start.format('Do MMM YYYY') }}
                            </td>
                            <td v-if="!job.first_time_slot">
                                -
                            </td>
                            <td>{{ job.status | capitalize }}</td>
                            <td>{{ job.updated_at.calendar() }}</td>
                            <td class="text-right">
                                <router-link
                                    :to="{ name: 'office.jobs.show', params: { id: job.id } }"
                                    title="View Job"
                                    class="row-icon"
                                >
                                    <i class="fa fa-eye" />
                                </router-link>
                                <router-link
                                    :to="{ name: 'office.jobs.edit', params: { id: job.id } }"
                                    title="Edit Job"
                                    class="row-icon"
                                >
                                    <i class="fa fa-pencil-alt" />
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <i
                    class="fa fa-spinner fa-pulse mb-2 mr-sm-2"
                    v-if="loading"
                />
                <pagination
                    :page="page"
                    :last-page="lastPage"
                    @pageChange="pageChange"
                />
                <span
                    class="pagination-stats"
                    v-if="total > 0"
                >Showing {{ from }} - {{ to }} out of {{ total }}</span>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <h3>Reports</h3>
                <router-link
                    :to="{name: 'office.jobs.report.missing_handover'}"
                    class="btn btn-info"
                >
                    Missing Job Sheet Report
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import JobService from "../../../services/JobService";
import Pagination from "../../PaginationComponent.vue";
import datePicker from "../../DateTimePickerComponent.vue";
import NProgress from "nprogress";

export default {
    components: {
        pagination: Pagination,
        "date-picker": datePicker
    },

    data: function() {
        return {
            loading: false,
            jobs: [],
            jobService: JobService,
            lastPage: 1,
            perPage: 50,
            from: 1,
            to: 1,
            total: 0,
            statuses: [
                {
                    name: "Unstarted",
                    value: "unstarted"
                },
                {
                    name: "Attended",
                    value: "attended"
                },
                {
                    name: "Invoiced",
                    value: "invoiced"
                }
            ]
        };
    },

    computed: {
        keyword: {
            get() {
                return this.$route.query.keyword || '';
            },
            set(value) {
                this.$router.replace({query: {
                    ...this.$route.query,
                    keyword: value,
                }});
            }
        },
        status: {
            get() {
                return this.$route.query.status || null;
            },
            set(value) {
                this.$router.replace({query: {
                    ...this.$route.query,
                    status: value,
                }});
            }
        },
        page: {
            get() {
                return parseInt(this.$route.query.page || '1');
            },
            set(value) {
                this.$router.replace({query: {
                    ...this.$route.query,
                    page: value,
                }});
            }
        },
        start_date: {
            get() {
                return this.$route.query.start_date || this.defaultStartDate();
            },
            set(value) {
                this.$router.replace({query: {
                    ...this.$route.query,
                    start_date: value,
                }});
            }
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.search();
            },
        }
    },

    mounted() {
        this.loading = true;
    },

    methods: {
        search: function() {
            NProgress.start();
            NProgress.set(0.7);
            NProgress.inc();
            setTimeout(() => {
                this.loading = true;
                let params = {
                    "filter[keyword]": this.keyword,
                    "filter[status]": this.status,
                    "filter[start_date]": this.start_date,
                    "page[number]": this.page,
                    include: ["customer", "site", "timeSlots", "firstTimeSlot"]
                };
                this.jobService
                    .search(params)
                    .then(response => {
                        this.jobs = response.data.data;
                        this.lastPage = response.data.last_page;
                        this.perPage = response.data.per_page;
                        this.total = response.data.total;
                        this.from = response.data.from;
                        this.to = response.data.to;
                        this.loading = false;
                    })
                    .catch(error => {
                        if (error.response.data.error !== undefined) {
                            alert("Error: " + error.response.data.error);
                        }
                        this.jobs = [];
                        this.total = 0;
                        this.from = 0;
                        this.to = 0;
                        this.page = 1;
                        this.lastPage = 1;
                        this.loading = false;
                    })
                    .then(() => {
                        NProgress.done();
                    });
            }, 1000);
        },

        searchChanged: function() {
            this.page = 1;
        },

        resetSearch: function() {
            this.$router.replace({query: {
                keyword: '',
                status: '',
                start_date: this.defaultStartDate(),
            }});
        },

        pageChange: function(page) {
            this.page = page;
        },

        dateChanged(date) {
            this.start_date = date ? window.moment(date).format('YYYY-MM-DD') : null;
        },
        defaultStartDate() {
            return window
                .moment()
                .startOf("month")
                .format("DD/MM/YYYY");
        }
    }
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/jobs/SearchComponent.vue